import {
  Component, OnInit, OnDestroy, HostListener, ChangeDetectorRef, inject
} from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil, filter, map, mergeMap } from 'rxjs/operators';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { AuthService } from './components/shared/service/auth.service';
import { SocketService } from './components/shared/service/socket.service';
import { ContatoService } from './components/shared/service/contato.service';
import { SideMenuComponent } from './components/shared/components/side-menu/side-menu.component';
import { TituloRotinaComponent } from './components/shared/components/app-titulo/titulo-component';
import { MatSidenav, MatSidenavContainer, MatSidenavContent } from '@angular/material/sidenav';
import { MatToolbarModule } from '@angular/material/toolbar';
import { CommonModule, NgClass } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { environment } from '../environments/environment';
import { MethodsUtils } from './components/shared/util/methods.utils';
import { Ambiente } from './components/shared/enum/ambiente.enum';
import { AtendimentoTransferenciaNotificacao } from './components/shared/model/atendimento/atendimento-transferencia-notificacao.model';
import { animate, style, transition, trigger } from '@angular/animations';
import { CHAT_TRANSFERENCIA_STORAGE_KEY } from './components/shared/util/chat-transferencia.constants';
import { LembreteNotificacaoService } from './components/shared/service/lembrete-notificacao.service';
import { LembreteNotificacaoComponent } from './components/shared/components/redesign/lembrete-notificacao/lembrete-notificacao.component';

interface AtendimentoTransferenciaNotificacaoView extends AtendimentoTransferenciaNotificacao {
  id: number;
  timerId?: ReturnType<typeof setTimeout>;
}

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    SideMenuComponent,
    RouterOutlet,
    MatSidenavContent,
    MatSidenavContainer,
    MatSidenav,
    NgClass,
    MatToolbarModule,
    TituloRotinaComponent,
    CommonModule,
    MatIconModule,
    LembreteNotificacaoComponent
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.css',
  animations: [
    trigger('toastAnimation', [
      transition(':enter', [
        style({
          opacity: 0,
          transform: 'translateX(120%) scale(.9)'
        }),
        animate(
          '350ms cubic-bezier(.22,1,.36,1)',
          style({
            opacity: 1,
            transform: 'translateX(0) scale(1)'
          })
        )
      ]),
      transition(':leave', [
        animate(
          '280ms ease-in',
          style({
            opacity: 0,
            transform: 'translateX(120%) scale(.85)'
          })
        )
      ])
    ])
  ]
})
export class AppComponent implements OnInit, OnDestroy {
  private static readonly MAX_NOTIFICACOES_TRANSFERENCIA = 4;
  private static readonly AUTO_DISMISS_MS = 5000;
  private static readonly PREFIXOS_ROTAS_PUBLICAS = ['/descadastro-email'];

  protected readonly platformBrowser = navigator.userAgent;
  isProd = environment.ambiente === Ambiente.PROD;
  isClicked = false;
  ambiente = environment.ambiente;
  isMobile = false;
  isMenuOpen = false;
  contatoSelecionado = false;
  titulo = '';
  idRotina?: number;
  designAntigo = true;
  notificacoesTransferencia: AtendimentoTransferenciaNotificacaoView[] = [];
  private notificacoesPendentes: AtendimentoTransferenciaNotificacao[] = [];

  private jaExecutouLogout = false;
  private readonly destroy$ = new Subject<void>();

  private readonly authService = inject(AuthService);
  private readonly socketService = inject(SocketService);
  private readonly lembreteNotificacaoService = inject(LembreteNotificacaoService);
  private readonly contatoService = inject(ContatoService);
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly title = inject(Title);

  ngOnInit(): void {
    this.escutarNotificacaoTransferencia();
    if (this.authService.logado()) {
      this.socketService.connect();
    }

    this.setViewHeight();
    this.atualizarEhMobile();

    this.contatoService.contatoSelecionado$
      .pipe(takeUntil(this.destroy$))
      .subscribe(contato => this.contatoSelecionado = contato !== null);

    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      map(() => this.activatedRoute),
      map(route => { while (route.firstChild) route = route.firstChild; return route; }),
      mergeMap(route => route.data),
      takeUntil(this.destroy$)
    ).subscribe(data => {
      this.lembreteNotificacaoService.aoNavegar();
      this.titulo       = data['titulo'] || '';
      this.title.setTitle(this.titulo ? `ClariTalk | ${this.titulo}` : 'ClariTalk');
      this.idRotina     = data['id'];
      this.designAntigo = data['designAntigo'] !== false;
      this.breakpointMobilePx = data['breakpointMobile'] ?? AppComponent.BREAKPOINT_MOBILE_PX;
      this.atualizarEhMobile();
      this.cdr.detectChanges();
    });
  }

  ngOnDestroy(): void {
    this.notificacoesTransferencia.forEach(notificacao => {
      if (notificacao.timerId) clearTimeout(notificacao.timerId);
    });
    this.destroy$.next();
    this.destroy$.complete();
    this.lembreteNotificacaoService.encerrar();
    this.socketService.disconnect();

    this.notificacoesPendentes = [];
  }

  get estaLogado(): boolean {
    const logado = this.authService.logado();

    if (!logado && !this.jaExecutouLogout && !this.rotaAtualEhPublica()) {
      this.jaExecutouLogout = true;
      this.authService.avisarSeTokenExpirou();
      Promise.resolve().then(() => {
        this.lembreteNotificacaoService.encerrar();
        this.authService.logout();
        this.socketService.disconnect();
      });
    }

    if (logado) this.jaExecutouLogout = false;
    return logado;
  }

  private rotaAtualEhPublica(): boolean {
    return AppComponent.PREFIXOS_ROTAS_PUBLICAS.some(prefixo => window.location.pathname.startsWith(prefixo));
  }

  get avisoTexto(): string {
    return this.isClicked
      ? `Aviso: Você está em ambiente de ${this.ambiente}. Plataforma: ${this.platformBrowser}`
      : `Aviso: Você está em ambiente ${this.ambiente === 'local' ? this.ambiente : 'de ' + this.ambiente}.`;
  }

  mostrarDetalhesPlataforma(): void {
    this.isClicked = !this.isClicked;
  }

  onMenuOpenChange(isOpen: boolean): void {
    this.isMenuOpen = isOpen;
  }

  @HostListener('document:click')
  onDocumentClick(): void {
    if (this.isMenuOpen) this.isMenuOpen = false;
  }

  @HostListener('window:resize')
  onResize(): void {
    this.setViewHeight();
    this.atualizarEhMobile();
    this.cdr.detectChanges();
  }

  @HostListener('window:focusout')
  onFocusOut(): void {
    if (!this.isMobile) return;
    setTimeout(() => {
      const campoFocado = document.activeElement?.matches('input, textarea, [contenteditable="true"]');
      if (!campoFocado && (window.scrollY !== 0 || document.documentElement.scrollTop !== 0)) window.scrollTo(0, 0);
    }, 100);
  }

  @HostListener('document:visibilitychange')
  onVisibilityChange(): void {
    if (document.visibilityState === 'visible') {
      this.liberarNotificacoesPendentes();
    }
  }

  calcularHeightPagina(): string {
    if (this.isMobile) return 'var(--altura-util-pagina)';
    if (!this.estaLogado || this.isProd) return '100vh';
    return 'calc(100vh - 16px)';
  }

  private static readonly BREAKPOINT_MOBILE_PX = 970;
  private breakpointMobilePx = AppComponent.BREAKPOINT_MOBILE_PX;

  private atualizarEhMobile(): void {
    this.isMobile = MethodsUtils.resolucaoPolegadas() < this.breakpointMobilePx / 96;
  }

  private setViewHeight(): void {
    document.documentElement.style.setProperty('--vh', `${window.innerHeight}px`);
  }

  removerNotificacaoTransferencia(id: number): void {
    const indice = this.notificacoesTransferencia.findIndex(notificacao => notificacao.id === id);
    if (indice < 0) return;

    const [notificacao] = this.notificacoesTransferencia.splice(indice, 1);
    if (notificacao?.timerId) clearTimeout(notificacao.timerId);
  }

  irParaConversa(notificacao: AtendimentoTransferenciaNotificacaoView, event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    localStorage.setItem(CHAT_TRANSFERENCIA_STORAGE_KEY, notificacao.numeroContato);
    this.removerNotificacaoTransferencia(notificacao.id);
    window.open('/chat', '_blank');
  }

  private get tabOculta(): boolean {
    return document.visibilityState === 'hidden';
  }

  private escutarNotificacaoTransferencia(): void {
    this.socketService
      .escutarTransferenciaAtendimento()
      .pipe(takeUntil(this.destroy$))
      .subscribe(notificacao => this.adicionarNotificacaoTransferencia(notificacao));
  }

  private liberarNotificacoesPendentes(): void {
    if (this.notificacoesPendentes.length === 0) return;
    const pendentes = [...this.notificacoesPendentes];
    this.notificacoesPendentes = [];
    pendentes.forEach(n => this.adicionarNotificacaoTransferencia(n));
  }

  private adicionarNotificacaoTransferencia(notificacao: AtendimentoTransferenciaNotificacao): void {
    if (this.tabOculta) {
      const existe = this.notificacoesPendentes
        .findIndex(n => n.numeroContato === notificacao.numeroContato);

      if (existe >= 0) {
        this.notificacoesPendentes[existe] = notificacao;
      } else {
        this.notificacoesPendentes.push(notificacao);
      }
      return;
    }
    const notificacaoView: AtendimentoTransferenciaNotificacaoView = {
      ...notificacao,
      id: Date.now() + Math.floor(Math.random() * 1000)
    };

    notificacaoView.timerId = setTimeout(
      () => this.removerNotificacaoTransferencia(notificacaoView.id),
      AppComponent.AUTO_DISMISS_MS
    );

    this.notificacoesTransferencia.unshift(notificacaoView);

    if (this.notificacoesTransferencia.length > AppComponent.MAX_NOTIFICACOES_TRANSFERENCIA) {
      const ultima = this.notificacoesTransferencia.pop();
      if (ultima?.timerId) clearTimeout(ultima.timerId);
    }
  }


  pausarTimerNotificacao(id: number): void {
    const notificacao = this.notificacoesTransferencia.find(n => n.id === id);
    if (!notificacao?.timerId) return;
    clearTimeout(notificacao.timerId);
    notificacao.timerId = undefined;
  }

  retomarTimerNotificacao(id: number): void {
    const notificacao = this.notificacoesTransferencia.find(n => n.id === id);
    if (!notificacao) return;
    notificacao.timerId = setTimeout(
      () => this.removerNotificacaoTransferencia(notificacao.id),
      AppComponent.AUTO_DISMISS_MS
    );
  }
}
